import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA, MdDialog, MdDialogConfig } from '@angular/material';

import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { TxRuleData, TransactionRule, ReviewStatus } from '../right.model';
import { TransactionMonitoringModel } from 'app/entities/transaction-monitoring/entity-transaction-monitoring.model';
import { EntityTransactionMonitoringService } from 'app/entities/transaction-monitoring/entity-transaction-monitoring.service';
import { EntityTransactionProfileService } from '../entity-transaction-profile.service';
import { FinanceService } from 'app/finance/finance.service';
import { BankAccount } from 'app/shared/models/bank-account.model';
import { RuleHistoryDialogComponent } from 'app/entities/transaction-monitoring/rule-history-dialog/rule-history-dialog.component';
import { AddTransactionRuleComponent } from './add-transaction-rule/add-transaction-rule.component';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmationdialog/confirmation-dialog.component';

interface MaxAmountTransaction {
  txnId: string;
  txnDate: string;
  amount: string;
  currency: string;
  counterPartyName: string;
  description: string;
  accountNumber: string;
  status: string;
}

@Component({
  selector: 'test-rule-transactions-dialog',
  templateUrl: './test-rule-transactions-dialog.component.html',
  styleUrls: ['./test-rule-transactions-dialog.component.css']
})
export class TestRuleTransactionsDialogComponent implements OnInit {
  ruleData: TxRuleData;
  entityIdDetails: EntityIdDetails;

  // Transaction selection
  transactions: TransactionMonitoringModel[] = [];
  filteredTransactions: TransactionMonitoringModel[] = [];
  displayedTransactions: TransactionMonitoringModel[] = [];
  selectedTransaction: TransactionMonitoringModel = null;

  fromDate: string;
  toDate: string;
  searchQuery: string = '';
  showAllTransactions = false;

  isLoading = false;
  isTesting = false;
  testResult: {
    matches: boolean;
    message: string;
    passedCriteria?: string[];
    failedCriteria?: string[];
    maxAmountTransactions?: MaxAmountTransaction[];
    maxAmountTotal?: string;
    maxAmountExcludedDuplicates?: MaxAmountTransaction[];
  } = null;

  // Expose enum to template
  ReviewStatus = ReviewStatus;

  constructor(
    public dialogRef: MdDialogRef<TestRuleTransactionsDialogComponent>,
    @Optional() @Inject(MD_DIALOG_DATA) public data: { rule: TxRuleData, entityIdDetails: EntityIdDetails },
    private transactionMonitoringService: EntityTransactionMonitoringService,
    private transactionProfileService: EntityTransactionProfileService,
    private financeService: FinanceService,
    private dialog: MdDialog
  ) {
    if (data) {
      this.ruleData = data.rule;
      this.entityIdDetails = data.entityIdDetails;
    }
  }

  ngOnInit() {
    this.initializeDateRange();
    if (this.entityIdDetails && this.entityIdDetails.clientId) {
      this.loadTransactions();
    }
  }

  private initializeDateRange() {
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setMonth(today.getMonth() - 1);

    this.fromDate = this.formatDateForInput(monthAgo);
    this.toDate = this.formatDateForInput(today);
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  onDateRangeChange() {
    this.loadTransactions();
  }

  onSearchChange() {
    this.applyFilters();
  }

  onShowAllChange() {
    this.applyFilters();
  }

  private loadTransactions() {
    if (!this.entityIdDetails || !this.entityIdDetails.clientId) {
      return;
    }

    this.isLoading = true;
    this.selectedTransaction = null;
    this.testResult = null;

    this.transactionMonitoringService
      .searchTransactions(this.entityIdDetails.clientId, this.fromDate, this.toDate)
      .subscribe(
        response => {
          const arrResult = response && response.arrResult ? response.arrResult : {};
          this.transactions = arrResult.transactions || [];
          this.applyFilters();
          this.isLoading = false;
        },
        error => {
          console.error('Error loading transactions for rule testing:', error);
          this.transactions = [];
          this.filteredTransactions = [];
          this.displayedTransactions = [];
          this.isLoading = false;
        }
      );
  }

  /**
   * Normalizes a name for comparison, matching the Java NameMatcher.normalize() logic
   * used in the transaction monitoring test profile wizard.
   */
  private normalizeName(name: string): string {
    if (!name || name.trim() === '') {
      return '';
    }

    let normalized = name.toLowerCase();

    // Normalize common abbreviations with periods to their non-period versions
    normalized = normalized.replace(/\b(s\.r\.l\.|s\.r\.l)\b/g, ' srl ');
    normalized = normalized.replace(/\b(l\.l\.c\.|l\.l\.c)\b/g, ' llc ');
    normalized = normalized.replace(/\b(i\.n\.c\.|i\.n\.c)\b/g, ' inc ');
    normalized = normalized.replace(/\b(c\.o\.|c\.o)\b/g, ' co ');
    normalized = normalized.replace(/\b(l\.t\.d\.|l\.t\.d)\b/g, ' ltd ');
    normalized = normalized.replace(/\b(b\.v\.|b\.v)\b/g, ' bv ');
    normalized = normalized.replace(/\b(c\.v\.|c\.v)\b/g, ' cv ');
    normalized = normalized.replace(/\b(n\.v\.|n\.v)\b/g, ' nv ');
    normalized = normalized.replace(/\b(g\.m\.b\.h\.|g\.m\.b\.h)\b/g, ' gmbh ');
    normalized = normalized.replace(/\b(a\.g\.|a\.g)\b/g, ' ag ');
    normalized = normalized.replace(/\b(k\.g\.|k\.g)\b/g, ' kg ');
    normalized = normalized.replace(/\b(o\.h\.g\.|o\.h\.g)\b/g, ' ohg ');
    normalized = normalized.replace(/\b(u\.g\.|u\.g)\b/g, ' ug ');
    normalized = normalized.replace(/\b(s\.a\.|s\.a)\b/g, ' sa ');
    normalized = normalized.replace(/\b(s\.a\.r\.l\.|s\.a\.r\.l)\b/g, ' sarl ');
    normalized = normalized.replace(/\b(s\.a\.s\.|s\.a\.s)\b/g, ' sas ');
    normalized = normalized.replace(/\b(s\.n\.c\.|s\.n\.c)\b/g, ' snc ');
    normalized = normalized.replace(/\b(s\.p\.a\.|s\.p\.a)\b/g, ' spa ');
    normalized = normalized.replace(/\b(s\.l\.|s\.l)\b/g, ' sl ');
    normalized = normalized.replace(/\b(p\.l\.c\.|p\.l\.c)\b/g, ' plc ');
    normalized = normalized.replace(/\b(l\.l\.p\.|l\.l\.p)\b/g, ' llp ');
    normalized = normalized.replace(/\b(l\.p\.|l\.p)\b/g, ' lp ');
    normalized = normalized.replace(/\b(a\.s\.|a\.s)\b/g, ' as ');
    normalized = normalized.replace(/\b(o\.y\.|o\.y)\b/g, ' oy ');
    normalized = normalized.replace(/\b(a\.b\.|a\.b)\b/g, ' ab ');
    normalized = normalized.replace(/\b(p\.t\.|p\.t)\b/g, ' pt ');
    normalized = normalized.replace(/\b(p\.t\.e\.|p\.t\.e)\b/g, ' pte ');
    normalized = normalized.replace(/\b(p\.v\.t\.|p\.v\.t)\b/g, ' pvt ');
    normalized = normalized.replace(/\b\(?pty\)?\b/g, ' pty ');

    normalized = normalized.replace(/\b(corp|corporation|limited|company|ltd|pty|sl|srl|llc|inc|co|bv|cv|nv|gmbh|ag|kg|ohg|ug|sa|sarl|sas|snc|spa|plc|llp|lp|as|oy|ab|pt|pte|pvt)\b/g, '');
    normalized = normalized.replace(/[^a-z0-9\s]/g, '');
    normalized = normalized.replace(/\s+/g, ' ').trim();

    return normalized;
  }

  private applyFilters() {
    if (!this.transactions || this.transactions.length === 0) {
      this.filteredTransactions = [];
      this.displayedTransactions = [];
      return;
    }

    // If "Show all transactions" is checked, skip counterparty filtering
    if (this.showAllTransactions) {
      // Only apply search query filter if provided
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const searchLower = this.searchQuery.toLowerCase().trim();
        this.filteredTransactions = this.transactions.filter(txn => {
          const description = (txn.communication || '').toLowerCase();
          const counterPartyName = (txn.counterPartyName || '').toLowerCase();
          const counterPartyAccNo = (txn.counterPartyAccNo || '').toLowerCase();
          return description.includes(searchLower) ||
            counterPartyName.includes(searchLower) ||
            counterPartyAccNo.includes(searchLower);
        });
      } else {
        // Show all transactions when checkbox is checked and no search query
        this.filteredTransactions = this.transactions;
      }
    } else {
      // Original behavior: filter by search query or counterparty name
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const searchLower = this.searchQuery.toLowerCase().trim();
        this.filteredTransactions = this.transactions.filter(txn => {
          const description = (txn.communication || '').toLowerCase();
          const counterPartyName = (txn.counterPartyName || '').toLowerCase();
          const counterPartyAccNo = (txn.counterPartyAccNo || '').toLowerCase();
          return description.includes(searchLower) ||
            counterPartyName.includes(searchLower) ||
            counterPartyAccNo.includes(searchLower);
        });
      } else if (this.ruleData && this.ruleData.counterPartyName) {
        // Default selection based on counterparty name from the rule
        const normalizedRuleCp = this.normalizeName(this.ruleData.counterPartyName);
        const ruleWords = normalizedRuleCp.split(/\s+/).filter(word => word.length > 0);

        this.filteredTransactions = this.transactions.filter(txn => {
          if (!txn.counterPartyName) {
            return false;
          }
          const normalizedTxnCp = this.normalizeName(txn.counterPartyName);
          const txnWords = normalizedTxnCp.split(/\s+/).filter(word => word.length > 0);

          const sameCounterparty = ruleWords.some(ruleWord =>
            ruleWord.length > 2 &&
            txnWords.some(txnWord =>
              txnWord.includes(ruleWord) || ruleWord.includes(txnWord)
            )
          );
          return sameCounterparty && this.amountFitsRule(txn);
        });
      } else {
        this.filteredTransactions = this.transactions;
      }
    }

    this.displayedTransactions = this.filteredTransactions;
  }

  private amountFitsRule(txn: TransactionMonitoringModel): boolean {
    const rule = this.getRule();
    if (!rule || rule.amountFrom == null || rule.amountTo == null || txn.amount == null) {
      return true;
    }
    const absolute = Math.abs(Number(txn.amount));
    const low = Math.min(Number(rule.amountFrom), Number(rule.amountTo));
    const high = Math.max(Number(rule.amountFrom), Number(rule.amountTo));
    return absolute >= low && absolute <= high;
  }

  selectTransaction(txn: TransactionMonitoringModel) {
    this.selectedTransaction = txn;
    this.testResult = null;
  }

  getRule(): TransactionRule {
    return this.ruleData ? this.ruleData.transactionRule : null;
  }

  getReviewStatusName(reviewStatus: ReviewStatus | number | string): string {
    if (reviewStatus === null || reviewStatus === undefined) {
      return 'N/A';
    }
    if (typeof reviewStatus === 'string') {
      return reviewStatus;
    }
    if (reviewStatus === ReviewStatus.DRAFT || reviewStatus === 0) {
      return 'DRAFT';
    } else if (reviewStatus === ReviewStatus.FINAL || reviewStatus === 1) {
      return 'FINAL';
    } else if (reviewStatus === ReviewStatus.REVIEW || reviewStatus === 2) {
      return 'REVIEW';
    }
    return 'N/A';
  }

  viewHistory(event: Event) {
    event.stopPropagation();

    const rule = this.getRule();
    if (!rule || !rule.id) {
      return;
    }

    const config = new MdDialogConfig();
    config.width = '900px';
    config.height = '80%';
    config.data = {
      rule: rule,
      clientId: this.entityIdDetails ? this.entityIdDetails.clientId : null
    };

    const historyDialogRef = this.dialog.open(RuleHistoryDialogComponent, config);

    historyDialogRef.afterClosed().subscribe(result => {
      if (result && result.restored) {
        // No automatic reload here; rules list is managed by parent component.
      }
    });
  }

  editRule(event: Event) {
    event.stopPropagation();
    const rule = this.getRule();
    if (!rule) {
      return;
    }

    const reviewStatusValue: any = rule.reviewStatus;
    const isFinal =
      reviewStatusValue === 'FINAL' ||
      reviewStatusValue === ReviewStatus.FINAL ||
      (typeof reviewStatusValue === 'string' &&
        reviewStatusValue.toUpperCase() === 'FINAL');

    if (isFinal) {
      const confirmConfig = new MdDialogConfig();
      confirmConfig.width = '400px';
      const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, confirmConfig);
      confirmDialogRef.componentInstance.title = 'Edit FINAL Rule';
      confirmDialogRef.componentInstance.message =
        'Are you sure you want to edit this rule? A new DRAFT version of this rule will be created.';

      confirmDialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.openEditDialog(rule);
        }
      });
    } else {
      this.openEditDialog(rule);
    }
  }

  private openEditDialog(rule: TransactionRule) {
    const txRuleData: TxRuleData = {
      id: this.ruleData ? this.ruleData.id : 0,
      title: this.ruleData ? this.ruleData.title : (rule.name || ''),
      counterPartyId: this.ruleData ? this.ruleData.counterPartyId : (rule.counterPartyId || 0),
      counterPartyName: this.ruleData ? this.ruleData.counterPartyName : (rule.counterPartyName || ''),
      date: this.ruleData ? this.ruleData.date : new Date(),
      agreementId: this.ruleData ? this.ruleData.agreementId : (rule.agreementId || 0),
      transactionRule: rule,
      isRight: this.ruleData ? this.ruleData.isRight : undefined
    };

    const config = new MdDialogConfig();
    const editDialogRef = this.dialog.open(AddTransactionRuleComponent, config);
    editDialogRef.componentInstance.selectedTxRule = txRuleData;
    editDialogRef.componentInstance.entityIdDetails = this.entityIdDetails;
    editDialogRef.componentInstance.agreementId = txRuleData.agreementId;
    editDialogRef.componentInstance.action = 'Edit';

    editDialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Reload the rule to show updated data
        this.reloadRule();
      }
    });
  }

  private reloadRule() {
    const rule = this.getRule();
    if (!rule || !rule.id || !this.entityIdDetails || !this.entityIdDetails.clientId) {
      return;
    }

    const originalRuleId = rule.id;
    const originalRuleName = rule.name;

    // Reload all rules for the client and find the updated one
    this.transactionProfileService.getTransactionRulesByClientId(this.entityIdDetails.clientId)
      .subscribe(rules => {
        // First try to find by the same ID (for DRAFT rules that were updated)
        let updatedRule = rules.find(r => r.id === originalRuleId);
        
        // If not found and we had a FINAL rule, it might have been edited to create a new DRAFT version
        // Try to find a rule with the same name that's DRAFT (the new version)
        if (!updatedRule && originalRuleName) {
          updatedRule = rules.find(r => {
            if (r.name !== originalRuleName) {
              return false;
            }
            const reviewStatusValue: any = r.reviewStatus;
            return reviewStatusValue === ReviewStatus.DRAFT || 
                   reviewStatusValue === 0 || 
                   (typeof reviewStatusValue === 'string' && reviewStatusValue.toUpperCase() === 'DRAFT');
          });
        }
        
        if (updatedRule && this.ruleData) {
          // Update the transaction rule in ruleData
          this.ruleData.transactionRule = updatedRule;
          // Also update other fields that might have changed
          if (updatedRule.counterPartyName) {
            this.ruleData.counterPartyName = updatedRule.counterPartyName;
          }
          if (updatedRule.name) {
            this.ruleData.title = updatedRule.name;
          }
          if (updatedRule.counterPartyId) {
            this.ruleData.counterPartyId = updatedRule.counterPartyId;
          }
          if (updatedRule.agreementId) {
            this.ruleData.agreementId = updatedRule.agreementId;
          }
        }
      }, error => {
        console.error('Error reloading rule after edit:', error);
      });
  }

  testTransactionMatch() {
    const rule = this.getRule();
    if (!rule || !rule.id || !this.selectedTransaction) {
      return;
    }

    this.isTesting = true;
    this.testResult = null;

    // Prepare test data with required fields
    const testData: any = {
      transactionId: this.selectedTransaction.txnId,
      profileId: rule.id || null
    };

    // If rule has a counterparty account number, try to find the bank account ID
    if (rule.counterPartyId && rule.counterPartyAccountNumber) {
      this.financeService.getBankAccountsForEntity(rule.counterPartyId)
        .subscribe(accounts => {
          // Ensure accounts is an array before calling .find()
          let accountsArray: BankAccount[] = [];
          if (Array.isArray(accounts)) {
            accountsArray = accounts;
          } else if (accounts && typeof accounts === 'object') {
            // If it's a single object, wrap it in an array
            accountsArray = [accounts];
          }
          
          const matchingAccount = accountsArray.find((acc: BankAccount) => 
            acc && acc.accountNumber === rule.counterPartyAccountNumber
          );
          if (matchingAccount) {
            testData.bankAccountId = matchingAccount.accountId;
          }
          this.executeTest(testData);
        }, error => {
          console.error('Error loading bank accounts for test:', error);
          // Continue with test even if bank account lookup fails
          this.executeTest(testData);
        });
    } else {
      // No bank account to look up, proceed with test
      this.executeTest(testData);
    }
  }

  private executeTest(testData: any) {
    this.transactionMonitoringService.testTransactionRuleMatch(testData)
      .subscribe(
        response => {
          this.isTesting = false;
          if (response && response.arrResult) {
            const result = response.arrResult;
            this.testResult = {
              matches: result.matches === true,
              message: result.message || (result.matches ? 'Transaction matches the rule!' : 'Transaction does not match the rule.'),
              passedCriteria: result.passedCriteria || [],
              failedCriteria: result.failedCriteria || [],
              maxAmountTransactions: result.maxAmountTransactions || [],
              maxAmountTotal: result.maxAmountTotal,
              maxAmountExcludedDuplicates: result.maxAmountExcludedDuplicates || []
            };
          } else {
            this.testResult = {
              matches: false,
              message: 'Unable to test transaction match.',
              passedCriteria: [],
              failedCriteria: []
            };
          }
        },
        error => {
          this.isTesting = false;
          console.error('Error testing transaction match:', error);
          this.testResult = {
            matches: false,
            message: 'Error testing transaction match: ' + (error.message || 'Unknown error')
          };
        }
      );
  }

  makeRuleFinal() {
    const rule = this.getRule();
    if (!rule || !rule.id) {
      return;
    }

    // Check if rule is DRAFT
    const reviewStatusValue: any = rule.reviewStatus;
    const isDraft = reviewStatusValue === 'DRAFT' || 
                    reviewStatusValue === ReviewStatus.DRAFT ||
                    reviewStatusValue === 0 ||
                    (typeof reviewStatusValue === 'string' && reviewStatusValue.toUpperCase() === 'DRAFT');

    if (!isDraft) {
      console.warn('Rule is not DRAFT, cannot make it FINAL');
      return;
    }

    if (!this.entityIdDetails || !this.entityIdDetails.clientId) {
      alert('Client ID is required to make rule FINAL.');
      return;
    }

    // Create a TransactionRule with status set to FINAL
    const updatedRule: TransactionRule = {
      id: rule.id,
      accountNo: rule.accountNo,
      name: rule.name,
      txnDescription: rule.txnDescription,
      cashFlow: rule.cashFlow,
      amountFrom: rule.amountFrom,
      amountTo: rule.amountTo,
      maxAmount: rule.maxAmount,
      maxAmountPeriod: rule.maxAmountPeriod,
      maxAmountPeriodName: rule.maxAmountPeriodName,
      maxAmountPeriodFrom: rule.maxAmountPeriodFrom,
      maxAmountPeriodTo: rule.maxAmountPeriodTo,
      frequency: rule.frequency,
      frequencyPeriod: rule.frequencyPeriod,
      frequencyPeriodName: rule.frequencyPeriodName,
      frequencyPeriodFrom: rule.frequencyPeriodFrom,
      frequencyPeriodTo: rule.frequencyPeriodTo,
      counterPartyId: rule.counterPartyId,
      counterPartyName: rule.counterPartyName,
      counterPartyAccountNumber: rule.counterPartyAccountNumber,
      counterPartyAccounts: rule.counterPartyAccounts,
      originCountry: rule.originCountry,
      cashFlowName: rule.cashFlowName,
      originCountryName: rule.originCountryName,
      reviewStatus: ReviewStatus.FINAL,
      agreementId: rule.agreementId
    };

    // Call the save endpoint to update the rule status to FINAL
    this.transactionProfileService.updateTransactionRuleStatus(
      this.entityIdDetails.clientId,
      updatedRule
    ).subscribe(
      response => {
        if (response && response.flagResult) {
          // Update the rule status in the current rule data
          if (this.ruleData && this.ruleData.transactionRule) {
            this.ruleData.transactionRule.reviewStatus = ReviewStatus.FINAL;
          }
          alert('Rule status updated to FINAL successfully.');
        } else {
          alert('Failed to update rule status to FINAL.');
        }
      },
      error => {
        console.error('Error making rule FINAL:', error);
        alert('Error making rule FINAL: ' + (error.message || 'Unknown error'));
      }
    );
  }

  close() {
    this.dialogRef.close();
  }
}





// WEBPACK FOOTER //
// ./src/app/entities/transaction-profile/transaction-rule/test-rule-transactions-dialog.component.ts